import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, Loader2, Sparkles, Gauge } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 5 quick questions — each answer carries a point value (0–20)
const questions = [
  {
    id: "speed",
    q: "Wie schnell lädt Deine aktuelle Website?",
    options: [
      { label: "Unter 2 Sek — flott", v: 20 },
      { label: "2–4 Sek — okay", v: 12 },
      { label: "Länger als 4 Sek", v: 4 },
      { label: "Keine Ahnung", v: 6 },
    ],
  },
  {
    id: "mobile",
    q: "Sieht sie auf dem Handy gut aus?",
    options: [
      { label: "Ja, sauber & responsive", v: 20 },
      { label: "Brauchbar, aber nicht perfekt", v: 10 },
      { label: "Nein, ist verschoben", v: 2 },
      { label: "Habe keine Website", v: 0 },
    ],
  },
  {
    id: "seo",
    q: "Wirst Du bei Google gefunden, wenn man Dich sucht?",
    options: [
      { label: "Ja, erste Seite", v: 20 },
      { label: "Manchmal, weiter hinten", v: 10 },
      { label: "Eher nein", v: 4 },
      { label: "Habe nie geprüft", v: 6 },
    ],
  },
  {
    id: "conversion",
    q: "Kommen über die Website regelmäßig Anfragen rein?",
    options: [
      { label: "Ja, läuft solide", v: 20 },
      { label: "Sehr wenige", v: 8 },
      { label: "Praktisch keine", v: 2 },
      { label: "Habe keine Website", v: 0 },
    ],
  },
  {
    id: "design",
    q: "Repräsentiert das Design Dein Business gut?",
    options: [
      { label: "Ja, voll auf der Höhe", v: 20 },
      { label: "Geht so", v: 10 },
      { label: "Wirkt veraltet", v: 4 },
      { label: "Habe keine Website", v: 0 },
    ],
  },
];

function gradeFromScore(score) {
  if (score >= 80) return { grade: "Stark unterwegs", color: "emerald", note: "Top — kleine Optimierungen reichen." };
  if (score >= 60) return { grade: "Solide Basis", color: "amber",   note: "Mit gezielten Schritten holst Du noch viel raus." };
  if (score >= 40) return { grade: "Ausbaufähig",   color: "orange",  note: "Es gibt einige Hebel, die schnell wirken." };
  return                  { grade: "Hoher Bedarf",  color: "rose",    note: "Hier lohnt sich ein Neustart — sprich mich an." };
}

export default function QuickCheck() {
  const [step, setStep] = useState(0); // 0..questions.length = result form
  const [answers, setAnswers] = useState({}); // {id: {label, v}}
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const totalSteps = questions.length;
  const score = Object.values(answers).reduce((s, a) => s + (a?.v || 0), 0);
  const { grade, color, note } = gradeFromScore(score);
  const progress = step === 0 && !Object.keys(answers).length
    ? 0
    : Math.min(100, Math.round(((Math.min(step, totalSteps)) / totalSteps) * 100));

  const colorMap = {
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    orange: "bg-orange-500",
    rose: "bg-rose-500",
  };
  const badgeMap = {
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
    orange: "bg-orange-100 text-orange-800 border-orange-200",
    rose: "bg-rose-100 text-rose-800 border-rose-200",
  };

  const pickAnswer = (q, opt) => {
    setAnswers((prev) => ({ ...prev, [q.id]: { label: opt.label, v: opt.v } }));
    // auto-advance after short delay
    setTimeout(() => setStep((s) => Math.min(totalSteps, s + 1)), 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quickcheck`, {
        name,
        email,
        website: website || null,
        score,
        grade,
        answers: questions.map((q) => ({
          question: q.q,
          answer: answers[q.id]?.label || "—",
        })),
      });
      setDone(true);
      toast.success("Dein Ergebnis ist unterwegs — schau in Deinen Posteingang.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Etwas ist schiefgelaufen. Probier es nochmal.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setName("");
    setEmail("");
    setWebsite("");
    setDone(false);
  };

  return (
    <section
      id="quick-check"
      data-testid="quickcheck-section"
      className="py-20 md:py-28 bg-gradient-to-b from-zinc-50/60 via-white to-white"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3.5 py-1.5 text-xs font-medium text-zinc-700">
            <Sparkles size={12} /> Kostenloser 60-Sekunden-Check
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-zinc-900 mt-5">
            Wie gut ist <span className="italic">Deine</span> Website wirklich?
          </h2>
          <p className="mt-4 text-zinc-500 leading-relaxed">
            5 Fragen. Persönliches Mini-Audit per Mail. Keine Spam, kein Account, kein Bullshit.
          </p>
        </div>

        <div
          data-testid="quickcheck-card"
          className="relative bg-white border border-zinc-100 rounded-3xl shadow-[0_24px_80px_-32px_rgba(0,0,0,0.18)] overflow-hidden"
        >
          {/* Progress bar */}
          <div className="h-1 bg-zinc-100">
            <motion.div
              className="h-full bg-zinc-900"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>

          <div className="p-8 md:p-12">
            <AnimatePresence mode="wait">
              {/* Questions */}
              {step < totalSteps && !done && (
                <motion.div
                  key={`q-${step}`}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.3 }}
                  data-testid={`quickcheck-step-${step}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold">
                      Frage {step + 1} / {totalSteps}
                    </div>
                    {step > 0 && (
                      <button
                        type="button"
                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                        data-testid="quickcheck-back-button"
                        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900"
                      >
                        <ArrowLeft size={12} /> Zurück
                      </button>
                    )}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-medium text-zinc-900 leading-snug">
                    {questions[step].q}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3 mt-7">
                    {questions[step].options.map((opt) => {
                      const active = answers[questions[step].id]?.label === opt.label;
                      return (
                        <button
                          key={opt.label}
                          type="button"
                          data-testid={`quickcheck-option-${step}-${opt.v}`}
                          onClick={() => pickAnswer(questions[step], opt)}
                          className={`text-left p-5 rounded-2xl border transition-all duration-200 hover:-translate-y-0.5 ${
                            active
                              ? "border-zinc-900 bg-zinc-900 text-white"
                              : "border-zinc-200 bg-white hover:border-zinc-400 text-zinc-800"
                          }`}
                        >
                          <div className="text-sm font-medium">{opt.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Result form */}
              {step === totalSteps && !done && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  data-testid="quickcheck-result"
                >
                  <div className="grid md:grid-cols-2 gap-10 items-center">
                    <div>
                      <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold">
                        Dein Ergebnis
                      </div>
                      <div className="mt-4 flex items-end gap-2">
                        <Gauge size={28} className="text-zinc-400 mb-1" />
                        <div className="font-display text-7xl font-light text-zinc-900 leading-none">
                          {score}
                        </div>
                        <div className="text-2xl text-zinc-400 font-light pb-2">/100</div>
                      </div>
                      <div className={`mt-4 inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium ${badgeMap[color]}`}>
                        {grade}
                      </div>
                      <p className="mt-4 text-zinc-600 leading-relaxed">{note}</p>
                      <div className="mt-5 h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${score}%` }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          className={`h-full ${colorMap[color]}`}
                        />
                      </div>
                    </div>

                    <form onSubmit={submit} className="space-y-3" data-testid="quickcheck-form">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Dein Name</label>
                        <input
                          required
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          data-testid="quickcheck-name-input"
                          className="mt-1.5 w-full h-11 px-4 rounded-xl bg-zinc-50 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:bg-white"
                          placeholder="Max Mustermann"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">E-Mail</label>
                        <input
                          required
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          data-testid="quickcheck-email-input"
                          className="mt-1.5 w-full h-11 px-4 rounded-xl bg-zinc-50 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:bg-white"
                          placeholder="du@beispiel.de"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Deine Website (optional)</label>
                        <input
                          type="url"
                          value={website}
                          onChange={(e) => setWebsite(e.target.value)}
                          data-testid="quickcheck-website-input"
                          className="mt-1.5 w-full h-11 px-4 rounded-xl bg-zinc-50 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:bg-white"
                          placeholder="https://…"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="quickcheck-submit-button"
                        className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-zinc-900 text-white text-sm font-medium px-6 py-3.5 hover:bg-zinc-800 transition-all hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
                      >
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                        {submitting ? "Wird gesendet…" : "Ergebnis per Mail erhalten"}
                      </button>
                      <p className="text-[11px] text-zinc-400 leading-relaxed">
                        Du erhältst Dein Ergebnis sofort + innerhalb 24h eine persönliche Mini-Auswertung von mir. Kein Newsletter, kein Spam.
                      </p>
                    </form>
                  </div>
                </motion.div>
              )}

              {/* Thank you */}
              {done && (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  data-testid="quickcheck-done"
                  className="text-center py-6"
                >
                  <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 mx-auto flex items-center justify-center">
                    <CheckCircle2 size={32} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-3xl font-light text-zinc-900 mt-5">
                    Check in der Mail.
                  </h3>
                  <p className="mt-3 text-zinc-500 max-w-md mx-auto leading-relaxed">
                    Dein Ergebnis ist soeben unterwegs an <strong className="text-zinc-900">{email}</strong>. Ich melde mich innerhalb von 24 Std. mit einer persönlichen Mini-Auswertung.
                  </p>
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="quickcheck-reset-button"
                    className="mt-7 text-sm text-zinc-600 underline underline-offset-4 hover:text-zinc-900"
                  >
                    Check nochmal starten
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
